import React, { useState, useEffect, startTransition } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { setLoginData } from "../../redux/slices/userSlice";
import MobileAside from "./MobileAside";
import { MdSpaceDashboard } from "react-icons/md";
import { FaClipboardList } from "react-icons/fa";
import { HiShoppingBag, HiDownload } from "react-icons/hi";
import { BsBarChartLineFill } from "react-icons/bs";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { IoCalendar } from "react-icons/io5";
import { BsBank2 } from "react-icons/bs";
import { downloadOrders } from "../../axios/api/downloadOrders";

const Aside = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredItemId, setHoveredItemId] = useState(null);
  const [activeItemId, setActiveItemId] = useState("/dashboard");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1028);

  const icons = [
    {
      id: 1,
      section: t("dashboard"),
      path: "/dashboard",
      svg: <MdSpaceDashboard className="w-6 h-6" />,
    },
    {
      id: 2,
      section: t("services"),
      path: "/services",
      svg: <FaClipboardList className="w-5 h-5" />,
    },
    {
      id: 3,
      section: t("orders"),
      path: "/orders",
      svg: <HiShoppingBag className="w-6 h-6" />,
    },
    {
      id: 4,
      section: t("statistics"),
      path: "/statistics",
      svg: <BsBarChartLineFill className="w-6 h-6" />,
    },
    {
      id: 5,
      section: t("calendar"),
      path: "/calendar",
      svg: <IoCalendar className="w-6 h-6" />,
    },
    {
      id: 7,
      section: t("finances"),
      path: "/finances",
      svg: <BsBank2 className="w-6 h-6" />,
    },
  ];

  const lang = location.pathname.split("/")[1] || "am";

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
    window.dispatchEvent(new Event("resize"));
  };

  useEffect(() => {
    const parts = location.pathname.split("/");
    const pagePath = "/" + (parts[2] || "dashboard");
    setActiveItemId(pagePath);
  }, [location.pathname]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1028);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleClick = (item) => {
    if (item === "/") {
      setLoginData(false);
      localStorage.removeItem("userPartnerData");

      startTransition(() => {
        navigate(`/${lang}`);
      });

      return;
    }

    setActiveItemId(item);

    startTransition(() => {
      navigate(`/${lang}${item}`);
    });
  };

  const handleIconClick = () => {
    setActiveItemId("/dashboard");

    startTransition(() => {
      navigate(`/${lang}/dashboard`);
    });
  };

  const onDownloadClick = async () => {
    try {
      const response = await downloadOrders();

      if (!response?.data?.download_url) {
        throw new Error("No download URL found");
      }

      const today = new Date();
      const formattedDate = `${String(today.getDate()).padStart(2, "0")}-${String(
        today.getMonth() + 1,
      ).padStart(2, "0")}-${today.getFullYear()}`;

      const fileName = `${t("orders")}_${formattedDate}.csv`;

      const fileRes = await fetch(response.data.download_url);
      const blob = await fileRes.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <>
      {isMobile ? (
        <div>
          <MobileAside icons={icons} />
        </div>
      ) : (
        <aside
          className={`sticky top-[20px] shadow-xl h-[calc(100vh-40px)] m-[20px] mr-0 overflow-hidden z-20 ${
            isCollapsed ? "w-[80px] rounded-[15px]" : "w-[264px] rounded-[25px]"
          } transition-all duration-800 ease-in-out flex flex-col`}
        >
          <img
            src="/assets/OrangeBackground.png"
            alt=""
            className="absolute inset-0 w-full h-full object-cover brightness-[0.8]"
          />

          {/* Gradient overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(238, 82, 34, 0.9) 100%, rgba(0, 0, 0, 0) 50%)",
            }}
          />

          <div
            className={`items-center justify-between gap-3 relative z-10 ${isCollapsed ? "w-[80px] grid px-3 py-3" : "w-full flex px-6 py-6"}`}
          >
            <img
              src={
                isCollapsed
                  ? "/assets/header-logo-white-small.png"
                  : "/assets/header-logo-white.png"
              }
              alt="my service"
              className="cursor-pointer transition-all duration-300 h-auto w-[165px]"
              onClick={handleIconClick}
            />
            {/* Collapse Button */}
            <button
              onClick={toggleSidebar}
              className="
              text-secondary_black rounded-full p-[5px] block-background-design dark:!bg-secondary_black
              focus:outline-none w-7 h-7 flex items-center justify-center
              mx-auto shadow-xl
              group
            "
            >
              {isCollapsed ? (
                <IoIosArrowForward className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1 fill-secondary_black dark:fill-white" />
              ) : (
                <IoIosArrowBack className="w-5 h-5 transition-transform duration-200 group-hover:-translate-x-1 fill-secondary_black dark:fill-white" />
              )}
            </button>
          </div>

          {/* MENU */}
          <div
            className={`flex flex-col items-center justify-center gap-3 ${isCollapsed ? "w-[80px] grid py-[20px]" : "w-full flex px-6 py-6"} relative z-10`}
          >
            {icons.map((item) => {
              const isActive = activeItemId === item.path;
              const isHovered = hoveredItemId === item.id;
              const activeOrHover = isActive || isHovered;

              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 py-2.5 px-5 cursor-pointer rounded-full  transition-colors border-[3px] border-transparent select-none ${
                    activeOrHover
                      ? "text-secondary_black shadow-xl block-background-design dark:!bg-secondary_black dark:text-white"
                      : "text-white hover:bg-white hover:text-secondary_black"
                  } ${isCollapsed ? "justify-center w-[54px] mx-auto h-[54px]" : "justify-start w-full h-[50px]"}`}
                  onMouseEnter={() => setHoveredItemId(item.id)}
                  onMouseLeave={() => setHoveredItemId(null)}
                  onClick={() => handleClick(item.path)}
                >
                  <div
                    className={`flex items-center ${
                      isCollapsed ? "w-8 h-8" : ""
                    }`}
                  >
                    {item.svg}
                  </div>

                  {!isCollapsed && (
                    <span
                      className={`font-[500] text-[16px] transition-opacity duration-800 ${
                        isCollapsed ? "opacity-0" : "opacity-100"
                      }`}
                    >
                      {item.section}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* FOOTER */}
          <div className="mt-auto flex justify-between py-6 px-6 relative z-10">
            <div
              onClick={onDownloadClick}
              className="relative w-full cursor-pointer group"
            >
              <button
                className={`
                left-1/2 -translate-x-1/2 z-10
                w-14 h-14 rounded-full flex items-center justify-center
                transition-all duration-200
                ${isCollapsed ? "top-0 relative !border-none" : "-top-7 absolute !border-primary !border-[6px]"}
                bg-white block-background-design dark:!bg-secondary_black dark:text-white
                group-hover:bg-secondary_black
                group-hover:text-white
              `}
              >
                <HiDownload
                  className={`
                  w-6 h-6
                  transition-transform duration-200
                  ${!isCollapsed ? "group-hover:translate-y-1" : ""}
                `}
                />
              </button>

              {/* Expanded Block */}
              {!isCollapsed && (
                <div className="block-background-design dark:!bg-secondary_black h-[130px] rounded-[15px] p-4 flex items-end justify-between shadow-xl pt-8">
                  {/* Bottom Left Text */}
                  <div className="flex flex-col">
                    <span className="font-[600] text-[14px] text-primary">
                      {t("download")}
                    </span>
                    <span className="text-[15px] font-[600] text-secondary_black dark:text-white">
                      {t("orders")}
                    </span>
                  </div>

                  {/* Right Image */}
                  <img
                    src="/assets/documents-illustration.png"
                    alt="Document Illustration"
                    className="
                    w-[145px] h-auto 
                    absolute right-[-40px] bottom-[-10px]
                    drop-shadow-lg
                    transition-all duration-300 ease-in-out
                    group-hover:-translate-y-2
                  "
                  />
                </div>
              )}
            </div>
          </div>
        </aside>
      )}
    </>
  );
};

export default Aside;
